import { type FC, useState } from "react";

import { Button, RadioInput } from "@fronterahealth/frontera-ui-components";

import { type RulePackageFields } from "@api/rest/types-and-hooks";
import { PayorTypeMultiSelect } from "@pages/QARules/PayorTypeMultiSelect";
import { PackageType, ReportCategory } from "@pages/QARules/constants";
import {
  PACKAGE_TYPE_BADGE,
  PACKAGE_TYPE_DESCRIPTION,
  packageTypeOf,
  reportCategoryOf,
  reportTypesFor,
} from "@pages/QARules/qaRuleLabels";

const INPUT_CLASS =
  "w-full rounded border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-globemallow-600";

const TYPE_ITEMS = Object.values(PackageType).map((type) => ({
  id: type,
  title: `${PACKAGE_TYPE_BADGE[type].label} — ${PACKAGE_TYPE_DESCRIPTION[type]}`,
}));

const CATEGORY_ITEMS = [
  { id: ReportCategory.ALL, title: "All report types" },
  { id: ReportCategory.ABA, title: "ABA (initial assessments and reassessments)" },
  { id: ReportCategory.DIAGNOSIS, title: "Diagnosis" },
];

interface PackageDetailsFormProps {
  idPrefix: string;
  initial?: RulePackageFields;
  payorTypes: string[];
  submitText: string;
  isPending?: boolean;
  /** Keep the package type as-is (editing from an org or template page). */
  lockPackageType?: boolean;
  onSubmit: (fields: RulePackageFields) => Promise<void>;
  onCancel: () => void;
}

/** Package name, type, payors, report category and LLM guidance — for create and edit. */
export const PackageDetailsForm: FC<PackageDetailsFormProps> = ({
  idPrefix,
  initial,
  payorTypes,
  submitText,
  isPending,
  lockPackageType,
  onSubmit,
  onCancel,
}) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [instructions, setInstructions] = useState(initial?.instructions ?? "");
  const [packageType, setPackageType] = useState<PackageType>(
    packageTypeOf(initial?.package_type ?? PackageType.CUSTOM),
  );
  const [selectedPayors, setSelectedPayors] = useState<string[]>(initial?.payor_types ?? []);
  const [category, setCategory] = useState<ReportCategory>(reportCategoryOf(initial?.report_types ?? []));
  // Only rewrite report types when the category was changed here: a package can
  // carry types that don't map to one category, and saving shouldn't widen them.
  const [categoryChanged, setCategoryChanged] = useState(false);

  const isInsurance = packageType === PackageType.INSURANCE;
  const canSubmit = name.trim() !== "" && (!isInsurance || selectedPayors.length > 0) && !isPending;

  const togglePayor = (value: string) =>
    setSelectedPayors((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const handleSubmit = () =>
    onSubmit({
      name: name.trim(),
      description,
      instructions,
      package_type: packageType,
      payor_types: isInsurance ? selectedPayors : [],
      report_types: categoryChanged ? reportTypesFor(category) : (initial?.report_types ?? []),
    });

  return (
    <div className="flex flex-col gap-3 rounded border border-border-medium bg-bg-default p-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="mb-1 block text-xs font-medium text-text-secondary">
          Name <span className="font-normal text-text-tertiary">— visible to users</span>
        </label>
        <input id={`${idPrefix}-name`} className={INPUT_CLASS} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-description`} className="mb-1 block text-xs font-medium text-text-secondary">
          Description <span className="font-normal text-text-tertiary">— visible to users</span>
        </label>
        <input
          id={`${idPrefix}-description`}
          className={INPUT_CLASS}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-instructions`} className="mb-1 block text-xs font-medium text-text-secondary">
          Instructions{" "}
          <span className="font-normal text-text-tertiary">
            (optional, LLM only) — use for guidance that doesn&apos;t fit a yes/no rule
          </span>
        </label>
        <textarea
          id={`${idPrefix}-instructions`}
          className={INPUT_CLASS}
          rows={3}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
        />
      </div>
      {!lockPackageType && (
        <RadioInput
          title="Package type"
          legend="Where this package applies"
          name={`${idPrefix}-type`}
          items={TYPE_ITEMS}
          selectedId={packageType}
          onChange={(e) => setPackageType(packageTypeOf(e.target.value))}
        />
      )}
      {isInsurance && <PayorTypeMultiSelect options={payorTypes} selected={selectedPayors} onToggle={togglePayor} />}
      <RadioInput
        title="Report category"
        legend="Which reports this package applies to"
        name={`${idPrefix}-category`}
        items={CATEGORY_ITEMS}
        selectedId={category}
        onChange={(e) => {
          setCategory(CATEGORY_ITEMS.find((c) => c.id === e.target.value)?.id ?? ReportCategory.ALL);
          setCategoryChanged(true);
        }}
      />
      <div className="flex gap-2">
        <Button
          variant="primary"
          buttonStyle="filled"
          size="sm"
          text={submitText}
          disabled={!canSubmit}
          onClick={handleSubmit}
        />
        <Button variant="secondary" buttonStyle="border" size="sm" text="Cancel" onClick={onCancel} />
      </div>
    </div>
  );
};
